import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useState, useMemo, type ComponentProps } from 'react'
import { SignInSheet } from '@/components/turn-tracker/sign-in-sheet'
import { StartOverModal } from '@/components/turn-tracker/start-over-modal'
import { useTurnTrackerQueries } from '@/components/turn-tracker/hooks/use-turn-tracker-queries'
import { useSessionMutations, type SignInRequest } from '@/components/turn-tracker/hooks/use-turn-tracker-session-mutations'
import { useSignInOffTodayConfirm } from '@/components/turn-tracker/hooks/use-sign-in-off-today-confirm'
import { SignInOffTodayConfirmModal } from '@/components/turn-tracker/sign-in-off-today-confirm-modal'
import { useEntryMutations } from '@/components/turn-tracker/hooks/use-turn-tracker-entry-mutations'
import type { SessionStaff, TurnTrackerState } from '@/components/turn-tracker/types'
import { SendBackConfirmModal } from '@/components/turn-tracker/send-back-confirm-modal'
import { BREAKPOINT_QUERY } from '@/hooks/breakpoint-queries'
import { useRealtimeTurnTracker } from '@/hooks/use-realtime-turn-tracker'
import { useRealtimeCalendar } from '@/hooks/use-realtime-calendar'
import { TurnsPageSkeleton } from '@/features/turns-board/turns-page-skeleton'
import { OperationsViewOnlyBanner, PageCanvas } from '@/components/layout'
import { canAccessWorkspacePath } from '@/components/layout/workspace-route-access'
import { useAccessContext, useCanWriteOperational, useOperationsViewOnly, useOrg } from '@/providers'
import { usePrefetchAllServiceOptionGroups } from '@/components/calendar/appointment-form/use-prefetch-all-service-option-groups'
import { ArrivalToastHost } from '@/features/check-in-arrivals/arrival-toast-host'
import { useWaitingArrivals } from '@/features/check-in-arrivals/use-waiting-arrivals'
import { toTicketServices } from '@/features/visit-ticket/visit-ticket-services'
import { useTurnsBoardNow } from '@/features/turns-board/use-turns-board-now'
import { useTurnsBoardMidnightRefresh } from '@/features/turns-board/use-turns-board-midnight-refresh'
import { salonDateKey } from '@/lib/salon-date-anchor'
import { TurnsBoardHeader } from '@/features/turns-board/turns-board-header'
import { TurnsBoardGrid } from '@/features/turns-board/turns-board-grid'
import type { TurnsBoardActions, TurnsBoardMenuContext } from '@/features/turns-board/turns-board-actions'
import { useTurnsCellActions } from '@/features/turns-board/use-turns-cell-actions'
import { TurnsSeatActionConfirm } from '@/features/turns-board/turns-seat-action-confirm'
import { usePendingTurnsWrites } from '@/features/turns-board/turns-write-mutation'
import { useTurnsSeatActions } from '@/features/turns-board/use-turns-seat-actions'
import { useStableTurnsBoardActions } from '@/features/turns-board/use-stable-turns-board-actions'
import { useMarketingAccess } from '@/features/marketing/use-marketing-access'
import { useSalonModules } from '@/hooks/use-salon-modules'
import { useSalonSetup } from '@/hooks/use-salon-setup'
import { salonDoneUsesPerks } from '@/features/salon-setup/salon-setup-page-meanings'
import { salonHasTurnsToPay, useTurnsToPay } from '@/features/turns-to-pay/use-turns-to-pay'
import { TurnsWaitingPanelHost } from '@/features/turns-waiting-panel/turns-waiting-panel-host'
import { TurnsNewTurnTicketHost } from '@/features/turns-board/turns-new-turn-ticket-host'
import { turnsBoardMode, turnsPageLayout } from '@/features/turns-board/turns-board-mode'
import type { TicketProvider } from '@/features/visit-ticket/turns-ticket-tech-source'
import { useTurnsPageTicket } from '@/features/visit-ticket/use-turns-page-ticket'
import type { TicketOpen } from '@/features/visit-ticket/visit-ticket-types'
import { cn } from '@/lib/utils'
import { useIdleAfter } from '@/hooks/use-idle-after'
import { useStableHandler } from '@/hooks/use-stable-handler'

export const Route = createFileRoute('/_auth/$orgId/turn-tracker')({
  component: TurnTrackerPage,
})

// Stable empties: the memoized board never re-renders for a fresh [].
const NO_GHOSTS: ComponentProps<typeof TurnsBoardGrid>['ghosts'] = []
const NO_NOT_SIGNED_IN: ComponentProps<typeof TurnsBoardGrid>['notSignedIn'] = []

function TurnTrackerPage() {
  const { orgId } = Route.useParams()
  const { orgId: clerkOrgId } = useOrg()
  const reportAccess = useMarketingAccess()
  const canOpenTurnSettings = canAccessWorkspacePath('settings/turn-tracker', useAccessContext())
  const navigate = useNavigate()
  // "Visit on Calendar" leaves the cell menus while the agency has Calendar switched off.
  const calendarOn = useSalonModules().modules.calendar

  // Real-time sync across devices (broadcasts use the Clerk org id; queries are keyed by the route param)
  useRealtimeTurnTracker(clerkOrgId)
  // Bookings move the board too ("Coming up", a client started on the Calendar).
  useRealtimeCalendar(clerkOrgId)
  // "Right now": tech status lines, Next in turn, Coming up, techs not signed in, header numbers.
  const boardNow = useTurnsBoardNow(clerkOrgId)
  // Alongside-POS salons ring finished clients up on their own POS: the panel's "To pay" tab + header chip. The turn
  // sheet (check-in off) has no To pay tab, so it never reads (or polls) it.
  const salonSetup = useSalonSetup()
  const { toPay } = useTurnsToPay(clerkOrgId, salonSetup.isResolved && salonHasTurnsToPay(salonSetup.setup))
  const doneUsesPerks = salonDoneUsesPerks(salonSetup)
  // Turns without Hayven Check-in: the board is a turn sheet — "+ Walk-in", times instead of client names on cells, no
  // Waiting list. Alone (Turns only) the sheet's ticket opens beside the board; with Calendar on the client panel stays
  // for "Coming up" (a booked client starts there). Neither the client panel nor the sheet's ticket until the salon's
  // setup is known (no client panel flash on a cold load; the org shell prefetches these settings).
  const boardMode = turnsBoardMode(salonSetup)
  const { turnSheet, sheetOnly } = boardMode
  // Agency team accounts watch the board live but change nothing here (the API refuses their writes too):
  // no ticket opens, and the header, board and Waiting panel offer no actions.
  const viewOnly = useOperationsViewOnly()
  const canWriteOperational = useCanWriteOperational() && !viewOnly

  // UI State
  const [state, setState] = useState<TurnTrackerState>({
    isSignInSheetOpen: false,
    isStartOverConfirmOpen: false,
  })
  // "Send back" confirm for a turn that follows a visit (the visit returns to Waiting / a walk-in visit is removed);
  // `removeTurn` = the turn sheet's "Remove turn" on a walk-in visit.
  const [sendBack, setSendBack] = useState<{ entryId: string; appointmentId: string; techFirstName: string; removeTurn: boolean } | null>(null)

  // Queries (services are picked on the visit ticket, not on the page)
  const queries = useTurnTrackerQueries(orgId, clerkOrgId)
  const {
    sessionQuery, categoriesQuery, staffQuery, servicesQuery,
    session, orgTimezone,
    invalidateAll, servicesWithTurn, servicePriceMap, nextInTurnFor, serviceStaffCapabilityMap,
    serviceStaffCustomDurations,
  } = queries
  // The board day is the salon-local date: a screen left open overnight loads the new day's board at midnight.
  // (`orgTimezone` = first location, primary first — the server's board day uses the primary location too.)
  useTurnsBoardMidnightRefresh(clerkOrgId, orgTimezone)

  const serviceCategories = useMemo(
    () => ((categoriesQuery.data as any)?.data || []).map((c: any) => ({ id: c.id, name: c.name, sortOrder: c.sortOrder })),
    [categoriesQuery.data],
  )

  // Mutations: every board write joins the salon's Turns write queue and shows at the tap.
  const { signInMutation, startOverMutation, pauseStaffMutation, resumeStaffMutation, signOutMutation, reorderMutation } =
    useSessionMutations({ orgId, organizationId: clerkOrgId, staffQuery, invalidateAll, setState })

  const { deleteEntryMutation, markLateMutation } =
    useEntryMutations({ orgId, organizationId: clerkOrgId })

  const boardStaff = useMemo(() => (session?.staff ?? []).filter((s) => s.status !== 'SIGNED_OUT'), [session?.staff])
  const cellActions = useTurnsCellActions({
    orgId, organizationId: clerkOrgId, timezone: orgTimezone, staff: boardStaff, servicesWithTurn,
  })
  const hasPasscode = !!session?.config?.hasStartOverPasscode
  const seat = useTurnsSeatActions({ hasPasscode, signOut: signOutMutation, reorder: reorderMutation })
  // Only the row being signed in waits; the others stay tappable.
  const signingIn = usePendingTurnsWrites<SignInRequest>(clerkOrgId, 'sign-in')
  const signingInStaffIds = useMemo(() => new Set(signingIn.map((request) => request.staffId)), [signingIn])
  // A tech the schedule has off today is asked about first ("Sign in anyway?"), from the sheet and the board alike
  // (one question queue, so a refusal landing after the sheet closed still asks); confirming also writes today's
  // shift. `offToday` is missing from an API older than this web build.
  const offToday = boardNow.board?.offToday
  const offReasonByStaffId = useMemo(() => new Map((offToday ?? []).map((off) => [off.staffId, off.reason])), [offToday])
  const offTodaySignIn = useSignInOffTodayConfirm({
    offReasonOf: (staffId) => offReasonByStaffId.get(staffId) ?? null,
    signIn: (staffId, addToTodaySchedule) =>
      signInMutation.mutateAsync(addToTodaySchedule ? { staffId, addToTodaySchedule } : { staffId }),
  })

  // Turns with Hayven Check-in: clients who check in join the waiting list themselves. Read from the setup (the
  // shell's settings), never the arrivals payload, so the panel never opens as "Coming up" while arrivals load and a
  // salon without check-in never reads or polls a list that can only be empty.
  const turnsArrivals = salonSetup.isResolved && salonSetup.setup.turnsArrivals
  const waiting = useWaitingArrivals(clerkOrgId, { enabled: turnsArrivals })
  const [isWaitingDrawerOpen, setWaitingDrawerOpen] = useState(false)
  const closeWaitingDrawer = useCallback(() => setWaitingDrawerOpen(false), [])
  const closeSignInSheet = useCallback(() => setState((s) => ({ ...s, isSignInSheetOpen: false })), [])
  const signedInTechs = useMemo(
    () => boardStaff.map((row) => ({ staffId: row.staffId, firstName: row.staff.firstName })),
    [boardStaff],
  )
  const ticketServices = useMemo(
    () => toTicketServices(servicesWithTurn, servicesQuery.data?.data ?? []),
    [servicesWithTurn, servicesQuery.data],
  )
  const providers = useMemo<TicketProvider[]>(
    () => (staffQuery.data?.data?.items ?? []).map((s) => ({
      id: s.id, firstName: s.firstName, lastName: s.lastName ?? null, avatarUrl: s.avatarUrl ?? null,
    })),
    [staffQuery.data],
  )
  const askedTech = providers.find((p) => p.id === offTodaySignIn.pending?.staffId)
  const { ticket, openTicket, closeTicket, closeTicketForLeft, draftServicesFor, boardGhosts } = useTurnsPageTicket({
    organizationId: clerkOrgId,
    timezone: orgTimezone,
    arrivals: waiting.arrivals,
    isWaitingListSettled: waiting.query.isSuccess && !waiting.query.isFetching,
    services: ticketServices,
    boardStaff,
    techNowByStaffId: boardNow.byStaffId,
    providers,
    capabilities: serviceStaffCapabilityMap,
    customDurations: serviceStaffCustomDurations,
  })
  // Every service's add-on groups, so a service picked on the visit ticket shows its "+ Add Option" at once: read once
  // the board has painted and the browser is idle, or at the first ticket — never competing with the board's own
  // reads. Same write gate as the calendar (the endpoint rejects read-only roles).
  const boardPainted = useIdleAfter(!sessionQuery.isLoading)
  usePrefetchAllServiceOptionGroups(canWriteOperational && (boardPainted || !!ticket))

  // A row's "+" (or any ticket opened from the board) shows in the drawer below lg.
  const openTicketFromBoard = useStableHandler((open: TicketOpen) => {
    openTicket(open)
    // The turn sheet's own host picks column or drawer from the screen width itself.
    if (!sheetOnly && !window.matchMedia(BREAKPOINT_QUERY.lgUp).matches) setWaitingDrawerOpen(true)
  })
  const addWalkInFor = useStableHandler((row: SessionStaff) => openTicketFromBoard({ kind: 'blank', presetStaffId: row.staffId }))

  const openVisitInCalendar = useStableHandler((appointmentId: string) =>
    void navigate({ to: '/$orgId/calendar', params: { orgId }, search: { visit: appointmentId } }))

  // Every former bottom-bar action now lives on a cell menu, a tech menu or the header.
  const latestActions: Required<TurnsBoardActions> = {
    done: (row, entry) => void cellActions.markDone(row, entry),
    notDoneYet: (row, entry) => void cellActions.notDoneYet(row, entry),
    toggleRequested: cellActions.toggleRequested,
    changeTech: cellActions.changeTech,
    sendBack: (row, entry) => entry.appointmentId && setSendBack({
      entryId: entry.id,
      appointmentId: entry.appointmentId,
      techFirstName: row.staff.firstName,
      removeTurn: turnSheet && entry.visit?.isWalkIn === true,
    }),
    openInCalendar: openVisitInCalendar,
    removeEntry: (entry) => deleteEntryMutation.mutate(entry.id),
    pause: (row) => pauseStaffMutation.mutate(row.id),
    resume: (row) => resumeStaffMutation.mutate(row.id),
    skipTurn: (row) => markLateMutation.mutate(row.id),
    move: (row, direction) => seat.move(row.id, direction),
    signOut: (row) => seat.signOut(row.id),
    signIn: (staffId) => void offTodaySignIn.request(staffId).catch(() => {}),
  }
  // One stable object (each action calls the latest handler): the memoized board rows never re-render for it.
  const boardActions = useStableTurnsBoardActions(latestActions, calendarOn)
  // Turn sheet header + ticket: who is next in turn ("Next in turn · Thy") and how many techs are working.
  const boardTechs = boardNow.board?.techs
  const nextInTurnName = boardStaff.find((row) => boardNow.bySessionStaffId.get(row.id)?.isNextInTurn)?.staff.firstName ?? null
  const inServiceCount = boardTechs?.filter((tech) => tech.status === 'BUSY').length ?? 0

  const layout = turnsPageLayout(boardMode, { viewOnly, isTicketOpen: !!ticket })

  const menuContext = useMemo<TurnsBoardMenuContext>(() => ({
    turnSheet,
    organizationId: clerkOrgId,
    timezone: orgTimezone,
    servicesWithTurn,
    servicePrices: servicePriceMap,
    doneUsesPerks,
    techAvatar: (staffId) => {
      const row = boardStaff.find((seatRow) => seatRow.staffId === staffId)
      return row ? { fullName: [row.staff.firstName, row.staff.lastName].filter(Boolean).join(' '), avatarUrl: row.staff.avatarUrl } : null
    },
  }), [turnSheet, clerkOrgId, orgTimezone, servicesWithTurn, servicePriceMap, doneUsesPerks, boardStaff])

  // Loading state
  if (sessionQuery.isLoading) {
    return (
      <PageCanvas className="flex h-full min-h-0 flex-col">
        <TurnsPageSkeleton />
      </PageCanvas>
    )
  }

  return (
    <PageCanvas className="flex h-full min-h-0 flex-col">
      <ArrivalToastHost organizationId={clerkOrgId} timezone={orgTimezone} />
      <OperationsViewOnlyBanner />
      <TurnsBoardHeader
        timezone={orgTimezone}
        stats={boardNow.board?.stats}
        checkInEnabled={turnsArrivals}
        toPayCount={toPay ? toPay.visits.length : null}
        todaySummaryOrgId={reportAccess.canReport ? orgId : undefined}
        turnSettingsOrgId={canOpenTurnSettings ? orgId : undefined}
        waitingCount={turnsArrivals ? waiting.arrivals.length : null}
        onOpenWaiting={layout.clientsDrawerButton ? () => setWaitingDrawerOpen(true) : undefined}
        onNewTurn={layout.headerWalkIn ? () => openTicketFromBoard({ kind: 'blank', presetStaffId: null }) : undefined}
        inServiceCount={layout.turnSheetStats ? inServiceCount : undefined}
        onSignIn={viewOnly ? undefined : () => setState((s) => ({ ...s, isSignInSheetOpen: true }))}
        onStartOver={viewOnly ? undefined : () => setState((s) => ({ ...s, isStartOverConfirmOpen: true }))}
        isBusy={startOverMutation.isPending}
      />
      {/* Two zones: the board, and the Waiting panel (lg+; a drawer from the header below). The panel column sizes
          itself: 400px, or 780px from 1366px while a ticket's services menu is open (the board narrows). */}
      <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[minmax(0,1fr)_auto]">
        {/* Board — fills flush; a right hairline divides it from the panel. */}
        {/* Turn sheet alone with no ticket open: the board runs to the page edge, no divider. */}
        <section
          aria-label="Turn board"
          className={cn('relative min-h-0 min-w-0 overflow-hidden bg-clovie-surface', layout.boardDivider && 'lg:border-r lg:border-clovie-border')}
        >
          <TurnsBoardGrid
            staff={boardStaff}
            techNow={boardNow.bySessionStaffId}
            notSignedIn={boardNow.board?.notSignedInWithAppointments ?? NO_NOT_SIGNED_IN}
            context={menuContext}
            actions={boardActions}
            signingInStaffIds={signingInStaffIds}
            // A ticket left open when the account became view only must not leave previews on the board.
            ghosts={viewOnly ? NO_GHOSTS : boardGhosts}
            onAddWalkIn={addWalkInFor}
            readOnly={viewOnly}
          />
        </section>

        {layout.host === 'sheet' ? (
          <TurnsNewTurnTicketHost
            organizationId={clerkOrgId}
            ticket={ticket}
            services={ticketServices}
            categories={serviceCategories}
            nextInTurnName={nextInTurnName}
            onCloseTicket={closeTicket}
            readOnly={viewOnly}
          />
        ) : layout.host === 'panel' && (
        <TurnsWaitingPanelHost
          organizationId={clerkOrgId}
          timezone={orgTimezone}
          checkInEnabled={turnsArrivals}
          arrivals={waiting.arrivals}
          comingUp={boardNow.board?.comingUp}
          techNowByStaffId={boardNow.byStaffId}
          nextInTurnFor={nextInTurnFor}
          signedInTechs={signedInTechs}
          services={ticketServices}
          categories={serviceCategories}
          ticket={ticket}
          onOpenTicket={openTicket}
          onCloseTicket={closeTicket}
          onClientLeft={closeTicketForLeft}
          draftServicesFor={draftServicesFor}
          isDrawerOpen={isWaitingDrawerOpen}
          onDrawerClose={closeWaitingDrawer}
          onOpenVisit={calendarOn ? openVisitInCalendar : undefined}
          readOnly={viewOnly}
          toPay={toPay}
          doneUsesPerks={doneUsesPerks}
          boardTechs={boardTechs}
          turnSheet={turnSheet ? { nextInTurnName } : undefined}
        />
        )}
      </div>

      {/* Sign-in sheet: stays open between taps; each face shows its own result. */}
      <SignInSheet
        open={state.isSignInSheetOpen && !viewOnly}
        onClose={closeSignInSheet}
        staff={providers}
        bookedDay={boardNow.board && orgTimezone ? salonDateKey(new Date(boardNow.board.now), orgTimezone) : undefined}
        boardStaff={session?.staff ?? []}
        bookedToday={boardNow.board?.notSignedInWithAppointments}
        offToday={offToday}
        signingInStaffIds={signingInStaffIds}
        timezone={orgTimezone}
        onSignIn={offTodaySignIn.request}
        isQuestionOpen={!!offTodaySignIn.pending}
      />

      {/* "Sign in anyway?" for an off-day tech, from the sheet or the board's own Sign in. */}
      <SignInOffTodayConfirmModal
        open={!!offTodaySignIn.pending}
        tech={askedTech ? { firstName: askedTech.firstName, fullName: [askedTech.firstName, askedTech.lastName].filter(Boolean).join(' ') } : null}
        reason={offTodaySignIn.pending?.reason ?? 'DAY_OFF'}
        onCancel={offTodaySignIn.cancel}
        onConfirm={offTodaySignIn.confirm}
      />

      {/* Start Over Confirmation Modal */}
      <StartOverModal
        open={state.isStartOverConfirmOpen}
        onClose={() => setState((s) => ({ ...s, isStartOverConfirmOpen: false }))}
        hasPasscode={hasPasscode}
        isPending={startOverMutation.isPending}
        onConfirm={(passcode) => startOverMutation.mutateAsync(passcode)}
      />

      {/* Closes at the tap: the turn leaves the board at once; a refusal toasts and the board reloads. */}
      <SendBackConfirmModal
        appointmentId={sendBack?.appointmentId ?? null}
        techFirstName={sendBack?.techFirstName ?? null}
        removeTurn={sendBack?.removeTurn}
        isPending={false}
        onClose={() => setSendBack(null)}
        onConfirm={() => {
          if (sendBack) deleteEntryMutation.mutate(sendBack.entryId)
          setSendBack(null)
        }}
      />

      {/* Tech menu: sign out (and move, with a passcode) confirmation */}
      <TurnsSeatActionConfirm
        seatAction={seat.seatAction}
        staff={session?.staff ?? []}
        hasPasscode={hasPasscode}
        isPending={seat.isPending}
        run={seat.run}
        onClose={seat.close}
      />
    </PageCanvas>
  )
}
